import { z } from 'zod';
import { ActivationId } from './activation.ts';
import { EntityRef, Id, IdempotencyKey, IsoDateTime, JsonObject, TenantId } from './common.ts';
import { Delegation } from './delegation.ts';
import { ResourceScope } from './entitlement.ts';
import { PlatformError } from './errors.ts';

/**
 * Added by 20a: how an agent episode (the governed task behind an `agent` activity) ended, in human terms. Set by
 * the terminal task write together with `Task.summary`. `inconclusive` (clarification rounds exhausted, the agent
 * abstained) is distinct from `error` (validation, policy, tool or workflow failure).
 */
export const EpisodeOutcomeKind = z
  .enum(['succeeded', 'inconclusive', 'error', 'cancelled'])
  .meta({ id: 'EpisodeOutcomeKind' });
export type EpisodeOutcomeKind = z.infer<typeof EpisodeOutcomeKind>;

/** Added by 20a: one clarification question and the answer the requester gave. */
export const AnsweredQuestion = z
  .object({ question: z.string().min(1), answer: z.string() })
  .meta({ id: 'AnsweredQuestion' });
export type AnsweredQuestion = z.infer<typeof AnsweredQuestion>;

/** Added by 20a: terminal outcome of an agent episode (task `episodeOutcome`, activity `outcome`). */
export const EpisodeOutcome = z
  .object({
    kind: EpisodeOutcomeKind,
    /** One sentence for a human (same text as `Task.summary`). */
    reason: z.string().min(1).max(4000),
    /** `inconclusive`: the questions still open when the episode stopped. */
    openQuestions: z.array(z.string().min(1)).optional(),
    /** Clarification questions answered during the episode (from the task timeline), in order. */
    answered: z.array(AnsweredQuestion).optional(),
  })
  .meta({ id: 'EpisodeOutcome' });
export type EpisodeOutcome = z.infer<typeof EpisodeOutcome>;

/** Added by 26a (§4.1): bounds of a `SpawnContinuation` (signal and step payloads cap at 256 KiB). */
export const CONTINUATION_LIMITS = {
  textMax: 4000,
  answersMax: 50,
  effectsMax: 50,
  priorMax: 10,
  /** Added 2026-11-03 (waived continuation): at most this many waived questions. */
  waivedMax: 50,
} as const;

const ContinuationText = z.string().max(CONTINUATION_LIMITS.textMax);

/**
 * Added by 26a (§3.2b): the explicit context of a continuation run (`run#n`, n ≥ 2). Lives here (not in `process.ts`)
 * so `TaskRequest.continuation` can use it without an import cycle; `process.ts` uses it for `SpawnTaskRequest`.
 */
export const SpawnContinuation = z
  .object({
    round: z.number().int().min(2),
    prior: z
      .array(
        z.object({
          activationId: ActivationId,
          outcome: EpisodeOutcome,
          summary: ContinuationText.optional(),
        }),
      )
      .max(CONTINUATION_LIMITS.priorMax)
      .default([]),
    answers: z
      .array(
        z.object({
          obligationId: Id,
          question: ContinuationText.min(1),
          answer: JsonObject,
          answeredBy: z.string().min(1),
          at: IsoDateTime,
        }),
      )
      .max(CONTINUATION_LIMITS.answersMax)
      .default([]),
    /**
     * Added 2026-11-03: the questions of the previous round that were waived (the fact will not come). The next run
     * must not ask them again. Rows persisted before this field parse with `[]`.
     */
    waived: z
      .array(
        z.object({
          obligationId: Id,
          question: ContinuationText.min(1),
          waivedBy: z.string().min(1),
          reason: ContinuationText.min(1),
          at: IsoDateTime,
        }),
      )
      .max(CONTINUATION_LIMITS.waivedMax)
      .default([]),
    verifiedEffects: z
      .array(
        z.object({
          receiptId: Id,
          tool: z.string().min(1).max(200),
          target: ContinuationText,
          verifiedEffect: JsonObject,
        }),
      )
      .max(CONTINUATION_LIMITS.effectsMax)
      .default([]),
  })
  .meta({ id: 'SpawnContinuation' });
export type SpawnContinuation = z.infer<typeof SpawnContinuation>;
export type SpawnContinuationInput = z.input<typeof SpawnContinuation>;

/** A request for governed work. `taskId`, `tenantId` and `requestedBy` are set by the platform. */
export const TaskRequest = z
  .object({
    taskId: Id,
    tenantId: TenantId,
    /** Capability name, e.g. `procurement.supplier-onboarding`. */
    capability: z.string().min(1).max(200),
    capabilityVersion: z.string().min(1).max(50),
    domain: z.string().min(1).max(100),
    objective: z.string().min(1).max(4000),
    entityRefs: z.array(EntityRef).default([]),
    inputs: JsonObject.default({}),
    /** Artifact ids or object-store keys supplied with the request. */
    artifactRefs: z.array(z.string().min(1)).default([]),
    deadline: IsoDateTime.optional(),
    /** Action scopes the requester allows, e.g. `erp:supplier:create`. */
    requestedActionScope: z.array(z.string().min(1)).default([]),
    idempotencyKey: IdempotencyKey,
    /** Principal id of the requester. */
    requestedBy: z.string().min(1),
    /** Where the request came from, e.g. `workbench`, `api`, `portal`, `email`, `event`, `scheduler`. */
    channel: z.string().min(1).max(64),
    /** Added by 17a: the case this task works for. */
    caseId: Id.optional(),
    /** Added by 17a: the task that spawned this one (process-spawned tasks only). */
    parentTaskId: Id.optional(),
    /** Added by 17a: the process activation (`<activityId>#<iteration>`) that spawned this task. */
    activationId: ActivationId.optional(),
    /** Added by 17a: resource scope (pack-declared dimensions); verified against entitlements, never trusted. */
    scope: ResourceScope.optional(),
    /**
     * Added by 17a: the server-held delegation of a process-spawned task (output of `narrowDelegation`). Its own
     * children narrow against it. Set by the orchestrator, never by the requester.
     */
    delegation: Delegation.optional(),
    /**
     * Added by 26a (brief 26 §3.2b): what a continuation run (`run#n`, n ≥ 2) of `platform.agent-run` was given (copied
     * from `SpawnTaskRequest.continuation`; persisted in `core_tasks.tasks.continuation`). The runtime renders it as
     * its own context block after the typed state.
     */
    continuation: SpawnContinuation.optional(),
  })
  .meta({ id: 'TaskRequest' });
export type TaskRequest = z.infer<typeof TaskRequest>;

/** `TaskRequest` as accepted by `TaskStore.create`: `taskId` is generated when omitted. */
export const NewTaskRequest = TaskRequest.extend({ taskId: Id.optional() }).meta({ id: 'NewTaskRequest' });
export type NewTaskRequest = z.input<typeof NewTaskRequest>;

export const TaskState = z
  .enum([
    'queued',
    'running',
    'awaiting_input',
    'awaiting_approval',
    'blocked',
    'reconciling',
    'compensating',
    'succeeded',
    'failed',
    'cancelled',
    'expired',
  ])
  .meta({ id: 'TaskState' });
export type TaskState = z.infer<typeof TaskState>;

export const TASK_STATES: readonly TaskState[] = TaskState.options;

/** States from which no transition is legal. */
export const TASK_TERMINAL_STATES: readonly TaskState[] = ['succeeded', 'failed', 'cancelled', 'expired'];

/**
 * The single source of legal task transitions. Self-transitions are not listed; `TaskStore.transition`
 * treats `from === to` as an idempotent no-op so replayed workflow steps are harmless.
 * Cancellation is not legal from `reconciling`/`compensating`: committed effects must be resolved first.
 */
export const TASK_TRANSITIONS: Readonly<Record<TaskState, readonly TaskState[]>> = {
  queued: ['running', 'cancelled', 'expired', 'failed'],
  running: [
    'awaiting_input',
    'awaiting_approval',
    'blocked',
    'reconciling',
    'compensating',
    'succeeded',
    'failed',
    'cancelled',
    'expired',
  ],
  awaiting_input: ['running', 'cancelled', 'expired', 'failed'],
  awaiting_approval: ['running', 'cancelled', 'expired', 'failed'],
  blocked: ['running', 'reconciling', 'compensating', 'failed', 'cancelled'],
  reconciling: ['running', 'blocked', 'compensating', 'succeeded', 'failed'],
  compensating: ['blocked', 'failed', 'cancelled'],
  succeeded: [],
  failed: [],
  cancelled: [],
  expired: [],
};

export function isTerminalState(state: TaskState): boolean {
  return TASK_TERMINAL_STATES.includes(state);
}

export function isLegalTransition(from: TaskState, to: TaskState): boolean {
  return TASK_TRANSITIONS[from].includes(to);
}

/** Throws `PlatformError('conflict')` when `from → to` is not in `TASK_TRANSITIONS`. */
export function assertTransition(from: TaskState, to: TaskState): void {
  if (!isLegalTransition(from, to)) {
    throw new PlatformError('conflict', `Illegal task transition ${from} → ${to}`, {
      details: { from, to, allowed: [...TASK_TRANSITIONS[from]] },
    });
  }
}

export const TaskOutcome = z
  .enum(['completed', 'partially_completed', 'outcome_unknown'])
  .meta({ id: 'TaskOutcome' });
export type TaskOutcome = z.infer<typeof TaskOutcome>;

/** Added by 20a: the episode outcome kind of a terminal task state without a recorded outcome. */
export function episodeOutcomeKindFor(state: TaskState): EpisodeOutcomeKind | undefined {
  switch (state) {
    case 'succeeded':
      return 'succeeded';
    case 'cancelled':
      return 'cancelled';
    case 'failed':
    case 'expired':
      return 'error';
    default:
      return undefined;
  }
}

/**
 * Added by 20a: the episode outcome of a terminal task — the recorded one, else one derived from its state and
 * summary (tasks finished before 20a, or by a writer that did not record one).
 */
export function episodeOutcomeOf(task: {
  state: TaskState;
  summary?: string | undefined;
  episodeOutcome?: EpisodeOutcome | undefined;
}): EpisodeOutcome {
  if (task.episodeOutcome) return task.episodeOutcome;
  const reason = (task.summary ?? '').slice(0, 4000);
  return { kind: episodeOutcomeKindFor(task.state) ?? 'error', reason: reason || `Task ${task.state}` };
}

/** A task as stored: the request plus lifecycle fields. */
export const Task = TaskRequest.extend({
  state: TaskState,
  runId: Id.optional(),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
  completedAt: IsoDateTime.optional(),
  outcome: TaskOutcome.optional(),
  summary: z.string().optional(),
  /** Added by 20a: set with the terminal transition (see `EpisodeOutcome`). */
  episodeOutcome: EpisodeOutcome.optional(),
}).meta({ id: 'Task' });
export type Task = z.infer<typeof Task>;

export const TimelineKind = z
  .enum([
    'state_change',
    'workflow_step',
    'episode',
    'proposal',
    'approval',
    'action',
    'receipt',
    'clarification',
    'note',
    'error',
    'system',
  ])
  .meta({ id: 'TimelineKind' });
export type TimelineKind = z.infer<typeof TimelineKind>;

/** One human-readable entry on a task's timeline. */
export const TimelineEntry = z
  .object({
    id: Id,
    taskId: Id,
    at: IsoDateTime,
    kind: TimelineKind,
    /** Workflow step name when the entry belongs to a step, e.g. `runReasoningEpisode`. */
    step: z.string().optional(),
    title: z.string().min(1),
    detail: z.string().optional(),
    /** Principal id or workload name that caused the entry. */
    actor: z.string().optional(),
    data: JsonObject.optional(),
  })
  .meta({ id: 'TimelineEntry' });
export type TimelineEntry = z.infer<typeof TimelineEntry>;

/** Input to `TaskStore.appendTimeline`; `id`, `taskId` are set by the store and `at` defaults to now. */
export const TimelineEntryInput = TimelineEntry.omit({ id: true, taskId: true })
  .extend({ at: IsoDateTime.optional() })
  .meta({ id: 'TimelineEntryInput' });
export type TimelineEntryInput = z.input<typeof TimelineEntryInput>;
